import React, { useState } from 'react';
import api from '../../config/axios';
import AdminMenu from '../../components/admin/menu';
import EmailTemplateEditor from '../../components/EmailTemplateEditor';
import { RichTextEditor } from '../../components/RichTextEditor';

type MailingMode = 'single' | 'selected' | 'all';
type TabType = 'send' | 'history' | 'quota' | 'templates';

interface User {
  userId: number;
  email: string;
  fullName: string;
  departement: number;
}

const AdminMailing: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('send');
  const [mode, setMode] = useState<MailingMode>('single');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sendStats, setSendStats] = useState<any>(null);
  
  // History tab states
  const [mailingLogs, setMailingLogs] = useState<any[]>([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [quota, setQuota] = useState<any>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Charger la liste des utilisateurs
  React.useEffect(() => {
    if (mode === 'selected') {
      fetchUsers();
    }
  }, [mode]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mailing/users');
      setUsers(response.data.users);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement des utilisateurs' });
    } finally {
      setLoading(false);
    }
  };

  // Charger l'historique des envois
  const fetchMailingHistory = async (page: number = 1) => {
    try {
      setLoadingHistory(true);
      const response = await api.get(`/mailing/logs?page=${page}&limit=10`);
      setMailingLogs(response.data.data || []);
      setLogTotal(response.data.pagination?.total || 0);
      setLogPage(page);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement de l\'historique' });
    } finally {
      setLoadingHistory(false);
    }
  };

  // Charger la quota d'aujourd'hui
  const fetchQuota = async () => {
    try {
      const response = await api.get('/mailing/quota/today');
      setQuota(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement de la quota' });
    }
  };

  // Charger les données au changement d'onglet
  React.useEffect(() => {
    if (activeTab === 'history') {
      fetchMailingHistory(1);
      fetchQuota();
    }
  }, [activeTab]);

  // Filtrer les utilisateurs selon la recherche
  const filteredUsers = users.filter(user =>
    user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Valider le formulaire
  const validateForm = (): boolean => {
    if (!subject.trim()) {
      setMessage({ type: 'error', text: 'Le sujet est requis' });
      return false;
    }
    if (!content.trim()) {
      setMessage({ type: 'error', text: 'Le contenu est requis' });
      return false;
    }

    if (mode === 'single') {
      if (!recipientEmail.trim()) {
        setMessage({ type: 'error', text: 'Adresse email requise' });
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(recipientEmail)) {
        setMessage({ type: 'error', text: 'Adresse email invalide' });
        return false;
      }
    } else if (mode === 'selected') {
      if (selectedUsers.length === 0) {
        setMessage({ type: 'error', text: 'Sélectionnez au moins un utilisateur' });
        return false;
      }
    }

    return true;
  };

  // Gérer la sélection/désélection des utilisateurs
  const handleUserToggle = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
      );
  };

  // Sélectionner/désélectionner tous les utilisateurs filtrés
  const handleSelectAll = () => {
    const visibleIds = filteredUsers.map(u => u.userId);
    if (selectedUsers.length === visibleIds.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(visibleIds);
    }
  };

  // Envoyer les emails
  const handleSend = async () => {
    if (!validateForm()) return;

    setShowConfirm(false);
    setLoading(true);
    setMessage(null);

    try {
      let response;

      if (mode === 'single') {
        response = await api.post('/mailing/send-single', {
          subject,
          content,
          recipientEmail,
        });
      } else if (mode === 'selected') {
        response = await api.post('/mailing/send-selected', {
          subject,
          content,
          userIds: selectedUsers,
        });
      } else {
        response = await api.post('/mailing/send-all', {
          subject,
          content,
        });
      }

      setSendStats(response.data);
      setMessage({ type: 'success', text: response.data.message });

      // Réinitialiser le formulaire
      setSubject('');
      setContent('');
      setRecipientEmail('');
      setSelectedUsers([]);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Erreur lors de l\'envoi des emails';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Compter le nombre de destinataires
  const getRecipientCount = (): number => {
    if (mode === 'single') return 1;
    if (mode === 'selected') return selectedUsers.length;
    return users.length;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Menu latéral */}
      <AdminMenu />

      {/* Contenu principal */}
      <main className="flex-1 overflow-y-auto p-8">
        <div className="max-w-4xl mx-auto">
          {/* Titre */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Envoi de Mails</h1>
            <p className="text-gray-600">Envoyez des emails à vos utilisateurs</p>
          </div>

          {/* Messages */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {message.text}
            </div>
          )}

          {/* Statistiques d'envoi */}
          {sendStats && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-bold text-blue-900 mb-2">Résultats d'envoi</h3>
              <p className="text-blue-800">✅ Envoyés: {sendStats.sentCount || sendStats.recipientCount}</p>
              {sendStats.failedCount && <p className="text-blue-800">❌ Échoués: {sendStats.failedCount}</p>}
              {sendStats.totalCount && <p className="text-blue-800">📊 Total: {sendStats.totalCount}</p>}
            </div>
          )}

          {/* Onglets */}
          <div className="mb-8 flex gap-4 border-b border-gray-200">
            {(['send', 'history', 'quota', 'templates'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 font-semibold transition-colors border-b-2 ${
                  activeTab === tab
                    ? 'text-orange-600 border-orange-600'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
              >
                {tab === 'send' && '✉️ Envoyer'}
                {tab === 'history' && '📋 Historique'}
                {tab === 'quota' && '📊 Quota'}
                {tab === 'templates' && '📧 Templates'}
              </button>
            ))}
          </div>

          {/* TAB: SEND */}
          {activeTab === 'send' && (
            <>
              {/* Messages */}
              {message && (
                <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {message.text}
                </div>
              )}

              {/* Statistiques d'envoi */}
              {sendStats && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2">Résultats d'envoi</h3>
                  <p className="text-blue-800">✅ Envoyés: {sendStats.sentCount || sendStats.recipientCount}</p>
                  {sendStats.failedCount && <p className="text-blue-800">❌ Échoués: {sendStats.failedCount}</p>}
                  {sendStats.totalCount && <p className="text-blue-800">📊 Total: {sendStats.totalCount}</p>}
                </div>
              )}

              {/* Sélection du mode */}
              <div className="mb-8 grid grid-cols-3 gap-4">
                {(['single', 'selected', 'all'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => {
                      setMode(m);
                      setSendStats(null);
                      setMessage(null);
                    }}
                    className={`p-4 rounded-lg font-semibold transition ${
                      mode === m
                        ? 'bg-orange-500 text-white shadow-lg'
                        : 'bg-white text-gray-700 border border-gray-200 hover:border-orange-500'
                    }`}
                  >
                    {m === 'single' && '✉️ Adresse unique'}
                    {m === 'selected' && '👥 Sélection'}
                    {m === 'all' && '📢 Tous les utilisateurs'}
                  </button>
                ))}
              </div>

              {/* Formulaire */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                {/* Sujet */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sujet</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Entrez le sujet de l'email"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all outline-none"
                    disabled={loading}
                  />
                </div>

                {/* Mode SINGLE */}
                {mode === 'single' && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse email</label>
                    <input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="exemple@email.com"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all outline-none"
                      disabled={loading}
                    />
                  </div>
                )}

                {/* Mode SELECTED */}
                {mode === 'selected' && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Sélectionnez les utilisateurs ({selectedUsers.length} sélectionnés)
                    </label>

                    {/* Barre de recherche */}
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Rechercher par nom ou email..."
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all outline-none mb-4"
                    />

                    {/* Bouton Sélectionner tout */}
                    <button
                      onClick={handleSelectAll}
                      className="mb-4 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-semibold text-sm"
                      disabled={loading}
                    >
                      {selectedUsers.length === filteredUsers.length && filteredUsers.length > 0
                        ? '☐ Désélectionner tout'
                        : '☑ Sélectionner tout'}
                    </button>

                    {/* Liste des utilisateurs */}
                    <div className="border border-gray-200 rounded-lg h-64 overflow-y-auto bg-gray-50">
                      {loading ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
                        </div>
                      ) : filteredUsers.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          Aucun utilisateur trouvé
                        </div>
                      ) : (
                        filteredUsers.map((user) => (
                          <div
                            key={user.userId}
                            className="flex items-center p-3 border-b border-gray-200 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleUserToggle(user.userId)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.userId)}
                              onChange={() => {}}
                              className="mr-3 w-4 h-4 accent-orange-500 rounded cursor-pointer"
                            />
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{user.fullName}</p>
                              <p className="text-sm text-gray-600">{user.email}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Contenu */}
                <div className="mb-6" style={{ position: 'relative', zIndex: 0 }}>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Contenu de l'email</label>
                  <div style={{ position: 'relative', zIndex: 0, overflow: 'hidden' }}>
                    <RichTextEditor
                      value={content}
                      onChange={setContent}
                      placeholder="Entrez le contenu de l'email..."
                      height="350px"
                    />
                  </div>
                </div>

                {/* Info destinataires */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 font-semibold">
                    📊 Destinataires: <span className="text-xl text-blue-600">{getRecipientCount()}</span>
                  </p>
                </div>

                {/* Boutons d'action */}
                <div className="flex gap-4">
                  <button
                    onClick={() => setShowConfirm(true)}
                    disabled={loading || !subject || !content}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-bold hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Envoi en cours...
                      </span>
                    ) : (
                      '✉️ Envoyer'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setSubject('');
                      setContent('');
                      setRecipientEmail('');
                      setSelectedUsers([]);
                      setMessage(null);
                      setSendStats(null);
                    }}
                    disabled={loading}
                    className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            </>
          )}

          {/* TAB: HISTORY */}
          {activeTab === 'history' && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6">Historique des envois</h2>
              
              {loadingHistory ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                </div>
              ) : mailingLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">Aucun envoi pour le moment</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left py-3 px-4 font-bold">Date</th>
                        <th className="text-left py-3 px-4 font-bold">Sujet</th>
                        <th className="text-left py-3 px-4 font-bold">Mode</th>
                        <th className="text-center py-3 px-4 font-bold">Total</th>
                        <th className="text-center py-3 px-4 font-bold">✅ Succès</th>
                        <th className="text-center py-3 px-4 font-bold">❌ Échoués</th>
                        <th className="text-left py-3 px-4 font-bold">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mailingLogs.map((log) => (
                        <tr key={log.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="py-3 px-4">{new Date(log.createdAt).toLocaleString('fr-FR')}</td>
                          <td className="py-3 px-4">{log.subject}</td>
                          <td className="py-3 px-4">
                            <span className="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-xs font-semibold">
                              {log.mode === 'single' && '✉️ Unique'}
                              {log.mode === 'selected' && '👥 Sélection'}
                              {log.mode === 'all' && '📢 Tous'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center font-semibold">{log.totalRecipients}</td>
                          <td className="py-3 px-4 text-center text-green-600 font-semibold">{log.successCount || 0}</td>
                          <td className="py-3 px-4 text-center text-red-600 font-semibold">{log.failureCount || 0}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              log.status === 'completed' ? 'bg-green-100 text-green-800' :
                              log.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              log.status === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {log.status === 'pending' && '⏳ En attente'}
                              {log.status === 'in_progress' && '⏳ En cours'}
                              {log.status === 'completed' && '✅ Terminé'}
                              {log.status === 'failed' && '❌ Échoué'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {logTotal > 10 && (
                <div className="mt-6 flex justify-center gap-2">
                  <button
                    onClick={() => fetchMailingHistory(logPage - 1)}
                    disabled={logPage === 1}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
                  >
                    ← Précédent
                  </button>
                  <span className="px-4 py-2 text-gray-700">
                    Page {logPage} sur {Math.ceil(logTotal / 10)}
                  </span>
                  <button
                    onClick={() => fetchMailingHistory(logPage + 1)}
                    disabled={logPage * 10 >= logTotal}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
                  >
                    Suivant →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB: QUOTA */}
          {activeTab === 'quota' && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6">Quota d'aujourd'hui</h2>
              
              {quota ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Carte : Envoyés */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="text-4xl font-bold text-green-600 mb-2">{quota.sent}</div>
                      <div className="text-green-900 font-semibold">Emails envoyés</div>
                    </div>

                    {/* Carte : Maximum */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="text-4xl font-bold text-blue-600 mb-2">{quota.max}</div>
                      <div className="text-blue-900 font-semibold">Limite quotidienne</div>
                    </div>

                    {/* Carte : Restant */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                      <div className="text-4xl font-bold text-orange-600 mb-2">{quota.remaining}</div>
                      <div className="text-orange-900 font-semibold">Emails restants</div>
                    </div>
                  </div>

                  {/* Barre de progression */}
                  <div className="mt-8">
                    <div className="flex justify-between mb-2">
                      <span className="font-semibold text-gray-700">Utilisation</span>
                      <span className="font-bold text-gray-900">{quota.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          quota.percentage <= 50 ? 'bg-green-500' :
                          quota.percentage <= 80 ? 'bg-orange-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(quota.percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>Chargement de la quota...</p>
                </div>
              )}
            </div>
          )}

          {/* TAB: TEMPLATES */}
          {activeTab === 'templates' && (
            <EmailTemplateEditor 
              api={api}
              onTemplateCreated={() => {
                setMessage({ type: 'success', text: 'Template créé avec succès! 🎉' });
                setTimeout(() => setMessage(null), 3000);
              }}
            />
          )}
        </div>
      </main>

      {/* Modal de confirmation */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Confirmation d'envoi</h2>
            <p className="text-gray-700 mb-2">
              Êtes-vous sûr de vouloir envoyer cet email?
            </p>
            <p className="text-gray-700 mb-6">
              <strong>Destinataires:</strong> <span className="text-orange-600 font-bold">{getRecipientCount()}</span>
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleSend}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-bold hover:from-orange-600 hover:to-orange-700 transition-all"
              >
                ✓ Confirmer
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition-all"
              >
                ✕ Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMailing;
